import { Box, Grid, InputAdornment, Typography, styled } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import {
  getConvertAmountMax,
  getConvertInitial,
} from "store/slices/convert/convertSlice";
import { useEffect, useMemo, useState } from "react";

import { AdornmentButton } from "components/GeneralStyled";
import { BadgeShape } from "pages/BasicTrade/components/TradeForm/TradeInput";
import CoinSelectItem from "./Components/CoinSelectItem";
import ConvertFooter from "./Components/ConvertFooter";
import ConvertSide from "./Components/ConvertSide";
import ConvertTradeSkeleton from "components/Skeletons/ConvertTrade.Skeleton";
import PriceInput from "components/PriceInput";
import Select from "components/Select";
import { repeat } from "utils/helpers/interval";
import useDispatch from "hooks/useDispatch";
import { useSelector } from "react-redux";
import CoinDrawerSelect from "components/CoinDrawerSelect";

let getConvertAmountMaxInterval;

export const THEMES = {
  primary: "primary",
  secondary: "secondary",
};

function ConvertForm({ setRefreshHistory }) {
  const dispatch = useDispatch();
  const [buttonsKey, setButtonsKey] = useState(1);
  const [showConvertDetails, setShowConvertDetails] = useState(false);
  const [loadingAmountMax, setLoadingAmountMax] = useState(false);

  const handleResetButtons = () => {
    if (showConvertDetails === true) {
      setButtonsKey((v) => v + 1);
      setShowConvertDetails(false);
    }
  };

  const defaultValues = {
    youPay: 1,
    youReceive: 3,
    coinId: 1,
    coinMasterId: 3,
    sideId: 1,
    amountFrom: "",
    amountTo: "",
  };

  const methods = useForm({ defaultValues });
  const { reset, watch, setValue } = methods;
  const coinId = watch("coinId");
  const coinMasterId = watch("coinMasterId");
  const sideId = watch("sideId");
  const youPay = watch("youPay");
  const youReceive = watch("youReceive");
  const amountFrom = watch("amountFrom");
  const amountTo = watch("amountTo");

  const convertInitial = useSelector(
    (state) => state.convert.convertInitial?.data
  );
  const convertAmountMax = useSelector(
    (state) => state.convert.convertAmountMax?.data
  );

  const fromCoinList = useMemo(() => {
    const currentCoinFrom = convertInitial?.lisCoinFrom?.find(
      (coin) => coin?.id === youPay
    );

    return currentCoinFrom;
  }, [youPay, convertInitial]);

  const toCoin = useMemo(() => {
    const currentCoinTo = fromCoinList?.lisCoinTo?.find(
      (coin) => coin.id === youReceive
    );

    if (!currentCoinTo) {
      setValue("youReceive", fromCoinList?.lisCoinTo?.[0]?.id);
    }

    reset((formValues) => ({
      ...formValues,
      coinId: currentCoinTo?.coinId,
      coinMasterId: currentCoinTo?.coinMasterId,
      sideId: currentCoinTo?.sideId,
      amountFrom: "",
      amountTo: "",
    }));

    return currentCoinTo;
  }, [fromCoinList?.lisCoinTo, youReceive, setValue, reset]);

  const amountFromMax = (coin) => {
    if (coin === toCoin?.coinId) {
      return convertAmountMax?.availableBalanceCoin || 0;
    }
    return convertAmountMax?.availableBalanceCoinMaster || 0;
  };

  const youPaySelectedCoin = useMemo(() => {
    return convertInitial?.lisCoinFrom?.find((item) => item?.id === youPay)
  }, [youPay, convertInitial?.lisCoinFrom])

  const youReceiveSelectedCoin = useMemo(() => {
    return convertInitial?.lisCoinFrom?.find((item) => item?.id === youReceive)
  }, [youReceive, convertInitial?.lisCoinFrom])

  useEffect(() => {
    dispatch(getConvertInitial());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetInitialAmountMax = async () => {
    setLoadingAmountMax(true);
    await dispatch(getConvertAmountMax({ coinId, coinMasterId, sideId }));

    setLoadingAmountMax(false);
  };

  useEffect(() => {
    if (convertInitial?.validSeconds) {
      handleGetInitialAmountMax();

      clearInterval(getConvertAmountMaxInterval);

      getConvertAmountMaxInterval = repeat({
        action: () =>
          dispatch(getConvertAmountMax({ coinId, coinMasterId, sideId })),
        time: convertInitial?.validSeconds * 1000,
      });
    }

    return () => {
      clearInterval(getConvertAmountMaxInterval);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coinId, coinMasterId, convertInitial, sideId]);

  return (
    <>
      {convertInitial ? (
        <FormProvider {...methods}>
          <form>
            <ConvertFormBox>
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12}>
                  <Typography
                    mb={1}
                    color="text.alternate"
                    variant="h2"
                    fontWeight={400}
                    textAlign="center"
                  >
                    Convert Trade
                  </Typography>

                  <Typography
                    color="text.main"
                    variant="h4"
                    fontWeight={400}
                    textAlign="center"
                  >
                    Input the details of your trade request below
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <ConvertSide handleResetButtons={handleResetButtons} />
                </Grid>

                {toCoin?.extraUnitComment && (
                  <Grid item xs={12} align="center">
                    <BadgeShape>
                      <Typography
                        variant="subtitle1"
                        fontWeight={500}
                        color="text.main"
                        display="flex"
                        justifyContent="center"
                      >
                        {toCoin?.extraUnitComment}
                      </Typography>
                    </BadgeShape>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="label" color="text.main">
                    You Pay
                  </Typography>

                  <PriceInput
                    value={amountFrom}
                    onChange={(e) => {
                      handleResetButtons();
                      setValue("amountTo", "");
                      setValue("amountFrom", e);
                    }}
                    disabled={loadingAmountMax}
                    showPrefix={false}
                    maxDecimal={toCoin?.decimalPlacesCoinFrom || 8}
                    coinName=""
                    max={amountFromMax(youPay)}
                    min={toCoin?.minimumTradeAmountCoinFrom || 0}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <AdornmentButton
                            variant="text"
                            color="secondary"
                            disabled={loadingAmountMax}
                            onClick={() => {
                              handleResetButtons();
                              setValue("amountTo", "");
                              setValue("amountFrom", amountFromMax(youPay));
                            }}
                          >
                            Max
                          </AdornmentButton>

                          <CoinDrawerSelect
                            coinList={{ lisCoin: convertInitial?.lisCoinFrom }}
                            selectedCoin={youPaySelectedCoin}
                            onCoinClick={(coin) => {
                              handleResetButtons()
                              setValue("youPay", coin?.id)
                            }}
                            isTitle={false}
                          />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="label" color="text.main">
                    You Receive
                  </Typography>

                  <PriceInput
                    value={amountTo}
                    onChange={(e) => {
                      handleResetButtons();
                      setValue("amountFrom", "");
                      setValue("amountTo", e);
                    }}
                    showPrefix={false}
                    maxDecimal={toCoin?.decimalPlacesCoinTo || 8}
                    coinName=""
                    disabled={loadingAmountMax}
                    max={amountFromMax(youReceive)}
                    min={toCoin?.minimumTradeAmountCoinTo || 0}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">

                          <CoinDrawerSelect
                            coinList={{ lisCoin: fromCoinList?.lisCoinTo }}
                            selectedCoin={youReceiveSelectedCoin}
                            onCoinClick={(coin) => {
                              handleResetButtons()
                              setValue("youReceive", coin?.id)
                            }}
                            isTitle={false}
                          />

                          {/* <Select
                            items={fromCoinList?.lisCoinTo}
                            itemText="name"
                            itemValue="id"
                            dir="ltr"
                            value={youReceive}
                            maxHeight="200px"
                            marginTop="0"
                            onChange={(e) => {
                              handleResetButtons();
                              const val = e.target.value;
                              setValue("youReceive", val);
                            }}
                            customValue={(data) => (
                              <CoinSelectItem data={data} />
                            )}
                          /> */}
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <ConvertFooter
                  setRefreshHistory={setRefreshHistory}
                  key={buttonsKey}
                  setShowConvertDetails={setShowConvertDetails}
                  showConvertDetails={showConvertDetails}
                />
              </Grid>
            </ConvertFormBox>
          </form>
        </FormProvider>
      ) : (
        <ConvertTradeSkeleton />
      )}
    </>
  );
}

const ConvertFormBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: "8px",
  background: theme.palette.background.convertTrade,
  boxShadow: `0 0 5px 2px ${theme.palette.background.boxShadowConvert}`,
}));

export default ConvertForm;
